/**
 * La boîte d'une séance qui porte déjà une demande — un retard signalé, un remplacement demandé ou
 * accepté (`<TutorSessionNoticeDialog>`) — et ses deux écritures, sur « Ma journée » et dans la
 * semaine des lives : les deux pages la reçoivent en prop (`sessionNotice`), ce crochet la tient.
 *
 * Les écritures de l'ancienne carte (`renderButton` de `TutorLiveCardComponent`) : « Annuler la
 * demande » annule le retard (`cancelTutorDelay`) ou le remplacement pas encore accepté
 * (`cancelSubstitution`), puis relit le compte et les lives. Un refus 400 de `cancelSubstitution` (le
 * remplacement tient à une absence sur plusieurs séances) rouvre la boîte sur « Impossible
 * d'annuler » : elle s'est fermée au geste, la séance visée est gardée le temps de la réponse.
 */
import { useCallback, useRef, useState } from "react"
import { useDispatch } from "react-redux"

import { apiRequest } from "../../../../../../data/redux/actions/api"
import { authService } from "../../../../../../data/services/authService"
import { liveService } from "../../../../../../data/services/liveService"
import { substitutionProposalsService } from "../../../../../../data/services/substitutionProposalsService"
import { type TutorSessionNotice } from "../../../../../../design-system/tutor"
import { tutorSessionNoticeFor, type UITutorHomeSession } from "../../../../adapters/tutor/Home/tutorSessionAdapter"
import { failureStatus } from "../../../hooks/gestureTracking"
import { useApiMutation } from "../../../hooks/useApiMutation"

export type TutorSessionNoticeState = { notice: TutorSessionNotice; session: UITutorHomeSession; lateMinutes?: number }

/**
 * Ce que « Annuler la demande » de la boîte annule. La boîte se lit une fois sur la séance
 * (`tutorSessionNoticeFor` : le retard d'abord) ; un remplacement accepté et un refus n'annulent rien.
 */
function cancelKindOf(notice: TutorSessionNotice): "late" | "substitution" | null {
    if (notice === "late") return "late"
    return notice === "substitutionPending" ? "substitution" : null
}

export function useTutorSessionNotice(surface: string) {
    const dispatch = useDispatch()
    const [sessionNotice, setSessionNotice] = useState<TutorSessionNoticeState | null>(null)
    const cancellingRef = useRef<UITutorHomeSession | null>(null)

    const afterCancel = useCallback(() => {
        cancellingRef.current = null
        dispatch(apiRequest(authService.getUser) as never)
        dispatch(apiRequest(liveService.getLiveSessions) as never)
    }, [dispatch])

    const { start: startCancelDelay, gesture: cancelDelayGesture } = useApiMutation({
        service: substitutionProposalsService.cancelTutorDelay,
        gesture: "cancel_late",
        surface,
        skipWhilePending: true,
        onSuccess: afterCancel,
        onError: () => {
            cancellingRef.current = null
        },
    })
    const { start: startCancelSubstitution, gesture: cancelSubstitutionGesture } = useApiMutation({
        service: substitutionProposalsService.cancelSubstitution,
        gesture: "substitution.cancel",
        surface,
        skipWhilePending: true,
        onSuccess: afterCancel,
        onError: (_message, error) => {
            const session = cancellingRef.current
            cancellingRef.current = null
            if (session != null && failureStatus(error) === "400") setSessionNotice({ notice: "cannotCancel", session })
        },
    })

    /** Ouvre la boîte d'une séance qui porte une demande ; `false` : elle n'en porte pas, elle se gère. */
    const open = useCallback(
        (session: UITutorHomeSession): boolean => {
            const notice = tutorSessionNoticeFor(session)
            if (notice == null) return false
            // La boîte mène à l'écriture : son ouverture se mesure sur le même geste.
            const kind = cancelKindOf(notice)
            if (kind === "late") cancelDelayGesture.opened()
            else if (kind === "substitution") cancelSubstitutionGesture.opened()
            setSessionNotice({ notice, session, lateMinutes: session.lateMinutes })
            return true
        },
        [cancelDelayGesture, cancelSubstitutionGesture]
    )

    /** « Annuler la demande » de la boîte ouverte. */
    const cancelRequest = useCallback(() => {
        if (sessionNotice == null) return
        const { session, notice } = sessionNotice
        const kind = cancelKindOf(notice)
        const sent =
            kind === "late"
                ? startCancelDelay({ id: session.id }, { occurrence_date: session.occurrenceDate })
                : kind === "substitution"
                  ? startCancelSubstitution({ id: session.substitutionId })
                  : false
        // Gardée seulement si l'écriture part : une annulation encore en vol garde la sienne.
        if (sent) cancellingRef.current = session
    }, [sessionNotice, startCancelDelay, startCancelSubstitution])

    return {
        sessionNotice,
        open,
        cancelRequest,
        close: useCallback(() => setSessionNotice(null), []),
    }
}
